import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { images } from '../data/images';
import { services } from '../data/services';
import { container, focusRing } from '../utils/styles';
import { PageHeader } from '../components/shared/PageHeader';
import { CtaBand } from '../components/shared/CtaBand';
import { ServiceDetail } from '../components/services/ServiceDetail';
import { PricingTable } from '../components/services/PricingTable';
import { SpecializedCare } from '../components/services/SpecializedCare';
export function Services() {
  const {
    t
  } = useLanguage();
  useDocumentMeta('services');
  return <>
      <PageHeader crumb={t({
      en: 'Services',
      es: 'Servicios'
    })} title={t({
      en: 'In-Home Care Services in Northern Virginia',
      es: 'Servicios de Cuidado en el Hogar en el Norte de Virginia'
    })} intro={t({
      en: 'From a few hours of companionship to 24-hour dementia care, every service is delivered by trained, bilingual caregivers and built around your loved one’s routine.',
      es: 'Desde unas horas de compañía hasta cuidado de demencia de 24 horas, cada servicio lo brindan cuidadores capacitados y bilingües, adaptado a la rutina de tu ser querido.'
    })} data-id="element-567" data-mp="64g8n6cxch5a" />

      <section className="bg-white py-16 sm:py-20" aria-label={t({
      en: 'Our approach',
      es: 'Nuestro enfoque'
    })} data-id="element-568" data-mp="1kghjmxhj4lfe">
        <div className={`${container} grid gap-10 lg:grid-cols-2 lg:items-center lg:gap-16`} data-id="element-569" data-mp="1pej0dbrhg8np">
          <img src={images.care.src} alt={t(images.care.alt)} className="h-72 w-full rounded-2xl object-cover sm:h-[26rem]" data-id="element-570" data-mp="1ud407a2toqbm" />
          <div data-id="element-571" data-mp="1ze9rr4kqy7jk">
            <h2 className="text-3xl font-bold leading-tight text-ink sm:text-4xl" data-id="element-572" data-mp="1zbnovi8swg7o">
              {t({
              en: 'Care that fits the way your family lives',
              es: 'Cuidado que se adapta a la vida de tu familia'
            })}
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-ink-soft" data-id="element-573" data-mp="90swo649zmel">
              {t({
              en: 'Most families combine several services into one plan. A caregiver might help your mother bathe and dress in the morning, prepare lunch, give her medication reminders, and then take her for a walk. You only pay for the hours you need.',
              es: 'La mayoría de las familias combinan varios servicios en un solo plan. Una cuidadora puede ayudar a tu madre a bañarse y vestirse por la mañana, preparar el almuerzo, recordarle sus medicamentos y luego salir a caminar con ella. Solo pagas las horas que necesitas.'
            })}
            </p>
            <dl className="mt-8 grid grid-cols-2 gap-6 border-t border-mist pt-6" data-id="element-574" data-mp="1q9n1est8xeks">
              <div data-id="element-575" data-mp="24zucuoku5xbz">
                <dt className="text-sm text-ink-soft" data-id="element-576" data-mp="252giujs2w4s3">{t({
                  en: 'Scheduling',
                  es: 'Horarios'
                })}</dt>
                <dd className="mt-1 font-display text-xl font-bold text-ink" data-id="element-577" data-mp="203wyuox1p32d">{t({
                  en: 'Hourly to 24/7',
                  es: 'Por hora a 24/7'
                })}</dd>
              </div>
              <div data-id="element-578" data-mp="293jzh8hya4ud">
                <dt className="text-sm text-ink-soft" data-id="element-579" data-mp="29425gwuezxfh">{t({
                  en: 'Languages',
                  es: 'Idiomas'
                })}</dt>
                <dd className="mt-1 font-display text-xl font-bold text-ink" data-id="element-580" data-mp="2soty16fpwvnm">English · Español</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      <section className="bg-cream py-16 sm:py-20" aria-label={t({
      en: 'All services',
      es: 'Todos los servicios'
    })} data-id="element-581" data-mp="2jmkoojw6r8bt">
        <div className={`${container} grid gap-10 lg:grid-cols-12`} data-id="element-582" data-mp="2nn4l1l8xq7ww">
          <aside className="lg:col-span-3" data-id="element-583" data-mp="29whysskd2ahp">
            <nav className="lg:sticky lg:top-36" aria-label={t({
            en: 'Services on this page',
            es: 'Servicios en esta página'
          })} data-id="element-584" data-mp="29vgb9om5rvj3">
              <h2 className="font-sans text-sm font-semibold text-ink-soft" data-id="element-585" data-mp="1zzmxk1wpdvzq">{t({
                en: 'Jump to a service',
                es: 'Ir a un servicio'
              })}</h2>
              <ul className="mt-3 flex gap-2 overflow-x-auto pb-2 lg:flex-col lg:gap-0 lg:overflow-visible lg:border-l lg:border-mist lg:pb-0" data-id="element-586" data-mp="24z81kqqqcujb">
                {services.map(s => <li key={s.id} className="shrink-0" data-id="element-587" data-mp="1vwqeka393xv9">
                    <a href={`#${s.id}`} className={`block whitespace-nowrap rounded-full bg-white px-4 py-2 text-[0.95rem] font-medium text-ink hover:text-brand lg:-ml-px lg:rounded-none lg:border-l-2 lg:border-transparent lg:bg-transparent lg:py-2.5 lg:hover:border-brand ${focusRing}`} data-id="element-588" data-mp="1nx2ib5l1rmyi">
                      {t(s.name)}
                    </a>
                  </li>)}
                <li className="shrink-0" data-id="element-589" data-mp="157dppj4o4imb">
                  <a href="#pricing" className={`block whitespace-nowrap rounded-full bg-white px-4 py-2 text-[0.95rem] font-semibold text-brand lg:-ml-px lg:rounded-none lg:border-l-2 lg:border-transparent lg:bg-transparent lg:py-2.5 lg:hover:border-brand ${focusRing}`} data-id="element-590" data-mp="15484iez5u0vv">
                    {t({
                    en: 'Pricing',
                    es: 'Precios'
                  })}
                  </a>
                </li>
              </ul>
            </nav>
          </aside>
          <div className="lg:col-span-9" data-id="element-591" data-mp="2liq1upwemasm">
            {services.map(s => <ServiceDetail key={s.id} service={s} data-id="element-592" data-mp="7cjspua56n2p" />)}
          </div>
        </div>
      </section>

      <PricingTable data-id="element-593" data-mp="cabhiffhpt2o" />
      <SpecializedCare data-id="element-594" data-mp="h2uy7l9h54bp" />
      <CtaBand data-id="element-595" data-mp="h2cvc5sb3rut" />
    </>;
}